/**
 * Modal styles injection utility
 */

/**
 * Injects modal styles into the document
 */
export function injectModalStyles(
  classSuffix: string,
  getClassName: (baseName: string) => string
): void {
  const styleId = `tds-modal-styles-${classSuffix}`;
  if (document.getElementById(styleId)) {
    return;
  }

  const style = document.createElement('style');
  style.id = styleId;
  const overlayClass = getClassName('tds-modal-overlay');
  const containerClass = getClassName('tds-modal-container');
  const showClass = getClassName('tds-modal-show');
  const closingClass = getClassName('tds-modal-closing');
  const fullscreenClass = getClassName('tds-modal-fullscreen');
  const responsiveClass = getClassName('tds-modal-responsive');
  const headerClass = getClassName('tds-modal-header');
  const closeClass = getClassName('tds-modal-close');
  const bodyClass = getClassName('tds-modal-body');
  const loadingClass = getClassName('tds-modal-loading');
  const spinnerClass = getClassName('tds-spinner');
  const iframeClass = getClassName('tds-modal-iframe');
  const formClass = getClassName('tds-modal-form');

  style.textContent = `
    .${overlayClass} {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.75);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      opacity: 0;
      backdrop-filter: blur(0px);
      transition: opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1),
                  backdrop-filter 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      pointer-events: none;
    }

    .${overlayClass}.${showClass} {
      opacity: 1;
      backdrop-filter: blur(8px);
      pointer-events: auto;
    }

    .${overlayClass}.${closingClass} {
      opacity: 0;
      backdrop-filter: blur(0px);
    }

    .${containerClass} {
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      width: 90%;
      max-width: 600px;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transform: scale(0.9) translateY(20px);
      opacity: 0;
      transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
                  opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .${containerClass}.${responsiveClass} {
      width: 90%;
      max-width: 90%;
      height: auto;
      max-height: 90vh;
    }

    .${containerClass}.${fullscreenClass} {
      width: 100vw !important;
      height: 100vh !important;
      max-width: 100vw !important;
      max-height: 100vh !important;
      border-radius: 0 !important;
    }

    .${overlayClass}.${showClass} .${containerClass} {
      transform: scale(1) translateY(0);
      opacity: 1;
    }

    .${overlayClass}.${closingClass} .${containerClass} {
      transform: scale(0.95) translateY(10px);
      opacity: 0;
    }

    .${headerClass} {
      position: relative;
      padding: 16px 20px;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-shrink: 0;
    }

    .${closeClass} {
      background: transparent;
      border: none;
      font-size: 32px;
      line-height: 1;
      color: #6b7280;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 4px;
      transition: color 0.2s, background-color 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }

    .${closeClass}:hover {
      color: #1f2937;
      background-color: #f3f4f6;
    }

    .${closeClass}:focus {
      outline: 2px solid #667eea;
      outline-offset: 2px;
    }

    .${closeClass}:active {
      background-color: #e5e7eb;
    }

    .${bodyClass} {
      position: relative;
      min-height: 300px;
      display: flex;
      flex-direction: column;
      flex: 1;
      height: 100%;
    }

    .${containerClass}.${responsiveClass} .${bodyClass} {
      min-height: 0;
    }

    .${loadingClass} {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #ffffff;
      z-index: 1;
    }

    .${spinnerClass} {
      width: 48px;
      height: 48px;
      border: 4px solid #f3f4f6;
      border-top-color: #667eea;
      border-radius: 50%;
      animation: tds-spin-${classSuffix} 1s linear infinite;
      margin-bottom: 16px;
    }

    @keyframes tds-spin-${classSuffix} {
      to {
        transform: rotate(360deg);
      }
    }

    .${loadingClass} p {
      margin: 0;
      color: #6b7280;
      font-size: 14px;
    }

    .${iframeClass} {
      width: 100%;
      height: 100%;
      min-height: 300px;
      border: none;
      flex: 1;
    }

    .${containerClass}.${responsiveClass} .${iframeClass} {
      min-height: 0;
    }

    .${formClass} {
      display: none;
    }

    /* Mobile devices (up to 500px) */
    @media (max-width: 500px) {
      .${containerClass}:not(.${fullscreenClass}) {
        width: 100%;
        max-width: 100%;
        max-height: 100vh;
        border-radius: 0;
      }

      .${containerClass}.${responsiveClass} {
        width: 100% !important;
        max-width: 100% !important;
        height: 100vh !important;
        max-height: 100vh !important;
        min-height: 100vh !important;
        border-radius: 0 !important;
      }
    }

    /* Small tablets and large phones (501px - 768px) */
    @media (min-width: 501px) and (max-width: 768px) {
      .${containerClass}.${responsiveClass} {
        width: 90% !important;
        max-width: 600px !important;
        height: auto !important;
        max-height: 85vh !important;
        min-height: 500px !important;
      }
    }

    /* Tablets (769px - 1024px) */
    @media (min-width: 769px) and (max-width: 1024px) {
      .${containerClass}.${responsiveClass} {
        width: 70% !important;
        max-width: 500px !important;
        height: auto !important;
        max-height: 80vh !important;
        min-height: 450px !important;
      }
    }

    /* Desktop and HD screens (1025px - 1920px) */
    @media (min-width: 1025px) and (max-width: 1920px) {
      .${containerClass}.${responsiveClass} {
        width: 400px !important;
        max-width: 400px !important;
        height: 380px !important;
        max-height: 380px !important;
        min-height: 380px !important;
      }
    }

    /* Large screens (1921px and above) */
    @media (min-width: 1921px) {
      .${containerClass}.${responsiveClass} {
        width: 450px !important;
        max-width: 450px !important;
        height: 420px !important;
        max-height: 420px !important;
        min-height: 420px !important;
      }
    }
  `;
  document.head.appendChild(style);
}

/**
 * Removes modal styles from the document
 */
export function removeModalStyles(classSuffix: string): void {
  const styleId = `tds-modal-styles-${classSuffix}`;
  const styleElement = document.getElementById(styleId);
  if (styleElement) {
    styleElement.remove();
  }
}
